import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import TimingFormSection from "~/components/formSections/TimingFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useOrderTitleValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useOrderTitleValidationComparisonData.ts";
import { updateOrder } from "~/modules/project/api/order/orderApiDispatchers.ts";
import type { Order, UpdateOrderData } from "~/modules/project/api/order/orderTypes.ts";
import OrderBudgetFormSection from "~/modules/project/components/forms/formSections/OrderBudgetFormSection";
import OrderStatusFormSection from "~/modules/project/components/forms/formSections/OrderStatusFormSection";
import OrderTitleAndNumberFormSection
	from "~/modules/project/components/forms/formSections/OrderTitleAndNumberFormSection";
import { getOrderQuotaTopUpCents } from "~/modules/project/utils/orderUtils.ts";
import type { Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { convertLocalToUTCDate, convertUTCToLocalDate, formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";


type CreateOrderFormProps = {
	onSuccess: () => void;
	onCancel: () => void;
	orderData: Order;
	quota?: Quota;
};

interface UpdateOrderFormData
	extends Omit<
		UpdateOrderData,
		"projectId" | "projectPhaseId" | "confirmedAt" | "manDays" | "startDate" | "endDate"
	> {
	confirmed: boolean;
	endDate: Date;
	manDays: number;
	startDate: Date;
}

const UpdateOrderForm: React.FC<CreateOrderFormProps> = ({ onSuccess, onCancel, orderData, quota }) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const titleComparisonData = useOrderTitleValidationComparisonData({
		projectId: orderData.projectId,
		projectPhaseId: orderData.projectPhaseId,
	});
	const schema = useMemo(() => {
		return yup
			.object({
				budgetCents: yup.number().required(),
				confirmed: yup.boolean().required(),
				endDate: yup
					.date()
					.required()
					.min(yup.ref("startDate"), "Das Startdatum muss vor dem Enddatum liegen."),
				invoiceOrderNumberRequired: yup.boolean().required(),
				manDays: yup.number().typeError(t("form:validationErrors.number.requiredTypeError")).required(),
				orderNumber: yup.string().default(null),
				startDate: yup.date().required(),
				title: yup.string().required().unique(titleComparisonData, orderData.id),
			});
	}, [titleComparisonData, orderData]);

	const defaultValues = useMemo(() => {
		const manDays = parseFloat(orderData.manDays);

		return {
			budgetCents: orderData.budgetCents,
			confirmed: !!orderData.confirmedAt || false,
			endDate: orderData.endDate ? convertUTCToLocalDate(orderData.endDate) : new Date(),
			invoiceOrderNumberRequired: orderData.invoiceOrderNumberRequired,
			manDays,
			orderNumber: orderData.orderNumber || "",
			startDate: orderData.startDate ? convertUTCToLocalDate(orderData.startDate) : new Date(),
			title: orderData.title,
		};
	}, [orderData]);
	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isValid, isSubmitted },
		watch,
		setValue,
	} = useForm<UpdateOrderFormData>({
		mode: "onChange",
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateOrderFormData>(schema),
	});


	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isValid,
		isSubmitted,
		isLoading: busy,
		enableInitially: false,
	});

	const onSubmit: SubmitHandler<UpdateOrderFormData> = async (data: UpdateOrderFormData) => {
		setBusy(true);
		const orderUpdateData = {
			...data,
			projectId: orderData.projectId,
			projectPhaseId: orderData.projectPhaseId,
			startDate: formatDateToYYYYMMDD(data.startDate!),
			endDate: formatDateToYYYYMMDD(data.endDate!),
			confirmedAt: data.confirmed ? convertLocalToUTCDate(new Date()) : null,
		};

		try {
			await updateOrder({ projectId: orderData.projectId, orderId: orderData.id, orderData: orderUpdateData });
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};


	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Bestellung aktualisieren</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<OrderTitleAndNumberFormSection control={control}
													showOrderNumberInput={!quota} />
					<OrderStatusFormSection control={control} />
					<OrderBudgetFormSection budgetCentsFromQuotas={getOrderQuotaTopUpCents(orderData)}
											control={control}
											showQuotaHint={!!quota}
											watch={watch} />
					<TimingFormSection control={control}
									   watch={watch}
									   setValue={setValue}
									   errors={errors}
									   title={"Laufzeit"} />
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateOrderForm;
